// Helpers PUROS del uso del portal y del asistente. Sin React y sin i18n adentro: los textos de
// producto salen por el `t` que recibe cada componente, así todo esto se testea sin montar nada.
//
// Acá viven las decisiones de presentación que las TRES superficies comparten (la sección del tab
// Portal, la card del Panel y el ranking cross-cliente): qué estados existen, cómo se escala el
// gráfico de 12 semanas y qué filas arma la tabla de navegación. Están en un archivo propio porque
// son los números que vamos a calibrar mirando la pantalla, y porque el mismo gráfico se dibuja
// grande en el detalle y en miniatura en 71 filas del ranking: una sola geometría, dos tamaños.
//
// Regla que NO se rompe: el `state` de un cliente lo calcula el backend y acá sólo se valida. Un
// KPI que vive en tres pantallas se define una vez o las tres divergen.

import {daysSince, parsePanelDate, relativeTimeLabel} from '../client/panel_v11/panelFormatters';

export {daysSince, parsePanelDate, relativeTimeLabel};

// --- Estados ------------------------------------------------------------------------------------

/**
 * Los cinco estados que manda el backend (`PortalUsageStatsService.STATE_*`).
 *
 * `DORMANT` e `INVITED_UNUSED` son los dos que hoy no existen en la app y los que disparan la
 * acción comercial: dentro de la ventana los dos son cero consultas, pero "nunca entró" se
 * resuelve reenviando la invitación y "usó dos meses y paró" se resuelve llamando al cliente.
 */
export const USAGE_STATE = {
    ACTIVE: 'ACTIVE',
    DORMANT: 'DORMANT',
    INVITED_UNUSED: 'INVITED_UNUSED',
    NO_USERS: 'NO_USERS',
    MODULE_OFF: 'MODULE_OFF',
};

const KNOWN_STATES = Object.keys(USAGE_STATE).map(key => USAGE_STATE[key]);

/**
 * El estado del payload, validado. Un valor desconocido (backend más nuevo que el frontend)
 * devuelve `null` en vez de inventarse uno: la sección degrada a "sin datos" y no miente.
 *
 * NO recalcula nada. Si esta función algún día deduce un estado a partir de los contadores, la
 * card del Panel y el ranking van a poder contradecirla.
 */
export function resolveUsageState(usage) {
    const state = usage && usage.state;
    return KNOWN_STATES.indexOf(state) >= 0 ? state : null;
}

/** El estado tiene números para mostrar (los otros tres son pantallas de vacío con acción). */
export function stateHasActivity(state) {
    return state === USAGE_STATE.ACTIVE || state === USAGE_STATE.DORMANT;
}

// --- Serie semanal ------------------------------------------------------------------------------

// Largo FIJO, decidido en el backend: 12 puntos siempre, viejo → nuevo, con las semanas sin
// actividad en 0. El frontend no alinea semanas por fecha (menos aún en 71 filas del ranking).
export const WEEKS_IN_SERIES = 12;

/** La serie del payload saneada: 12 enteros >= 0, viejo → nuevo. Rellena o recorta si hace falta. */
export function normalizeWeeks(weeks) {
    const source = Array.isArray(weeks) ? weeks : [];
    const series = [];
    for (let i = 0; i < WEEKS_IN_SERIES; i++) {
        const value = Number(source[i]);
        series.push(Number.isFinite(value) && value > 0 ? Math.round(value) : 0);
    }
    return series;
}

export function weeksTotal(weeks) {
    return normalizeWeeks(weeks).reduce((acc, value) => acc + value, 0);
}

/**
 * Serie enteramente en cero. Importa porque un cliente sin uso NO se dibuja como una línea plana
 * en el piso: una línea plana se lee como un dato y no lo es (se muestra un guion).
 */
export function weeksAreEmpty(weeks) {
    return weeksTotal(weeks) === 0;
}

/**
 * Cuántas semanas SEGUIDAS con actividad viene encadenando, contando desde la última semana con
 * consultas hacia atrás.
 *
 * Se cuenta desde la última semana ACTIVA y no desde la semana en curso a propósito: la semana de
 * hoy casi siempre está a medio llenar, y arrancar ahí haría que un cliente con seis semanas
 * seguidas de uso muestre "0" un lunes a la mañana.
 */
export function weeksStreak(weeks) {
    const series = normalizeWeeks(weeks);
    let index = series.length - 1;
    while (index >= 0 && series[index] === 0) index--;
    let streak = 0;
    while (index >= 0 && series[index] > 0) {
        streak++;
        index--;
    }
    return streak;
}

/**
 * Los lunes de las últimas 12 semanas, viejo → nuevo — las etiquetas del eje X.
 *
 * Se calculan acá y no vienen del backend porque el backend arma su serie con el MISMO criterio y
 * con el offset que le manda este navegador (`tz_offset`): las dos listas son la misma. Mandar 12
 * fechas por cliente en el ranking sería repetir 71 veces un dato que el frontend ya sabe.
 */
export function weekStartDates(count = WEEKS_IN_SERIES, now = new Date()) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // `getDay()` es domingo=0; la semana del backend (`TruncWeek` de Postgres) arranca el lunes.
    const monday = new Date(today);
    monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));

    const dates = [];
    for (let offset = count - 1; offset >= 0; offset--) {
        const week = new Date(monday);
        week.setDate(monday.getDate() - (7 * offset));
        dates.push(week);
    }
    return dates;
}

/** "19/5" — la etiqueta corta del eje X. Día y mes sin cero a la izquierda, como el mockup. */
export function formatWeekTick(date) {
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    return `${date.getDate()}/${date.getMonth() + 1}`;
}

/**
 * "28 may" / "May 28" — la fecha absoluta de la última actividad, en el idioma de la UI.
 *
 * Convive con `relativeTimeLabel` en vez de reemplazarlo: para el cliente dormido las dos hacen
 * falta. "Hace 74 días" dice la urgencia pero no permite cruzarlo con nada; "28 may" permite
 * cruzarlo con lo que pasó ese día pero obliga a hacer la cuenta. Fecha inválida → `null`.
 *
 * El mes sale de `Intl` con el `language` de i18n y no de una lista en español: la key del texto
 * que lo rodea sí está traducida, así que un mes hardcodeado produce frases mitad y mitad.
 */
export function formatShortDate(value, language) {
    const date = parsePanelDate(value);
    if (!date) return null;
    return date.toLocaleDateString(language || undefined, {day: 'numeric', month: 'short'});
}

/**
 * "19 de mayo" / "May 19" — el mes largo, para la lectura de un punto del gráfico. Cae en medio de
 * una oración, así que en español va en minúscula (`Intl` ya lo devuelve así).
 */
export function formatLongDate(value, language) {
    const date = parsePanelDate(value);
    if (!date) return '';
    return date.toLocaleDateString(language || undefined, {day: 'numeric', month: 'long'});
}

/**
 * Un número con el separador decimal del idioma de la UI (`1,8` en español, `1.8` en inglés).
 * Hacerlo con un `.replace('.', ',')` deja las duraciones en coma también en inglés.
 */
export function formatDecimal(value, language, maximumFractionDigits = 1) {
    return Number(value).toLocaleString(language || undefined, {maximumFractionDigits});
}

// --- Gráfico grande -----------------------------------------------------------------------------

// La caja del SVG del detalle. Coordenadas fijas + `viewBox`: el gráfico se escala solo al ancho
// disponible sin recalcular nada.
export const CHART_BOX = {width: 820, height: 216, left: 34, right: 16, top: 32, bottom: 30};

/**
 * Las líneas horizontales del gráfico: piso, mitad y techo.
 *
 * La mitad se redondea a entero y la línea se dibuja EN ese entero — etiquetar "7" una línea que
 * está en 6,5 es un eje que miente. Con máximos chicos (1 o 2) la intermedia coincidiría con el
 * piso o con el techo, así que directamente no va.
 */
export function chartGridLevels(max) {
    const half = Math.round(max / 2);
    return (half > 0 && half < max) ? [0, half, max] : [0, max];
}

/**
 * La geometría del gráfico de consultas por semana, lista para pintar.
 *
 * Dos decisiones del diseño que viven acá porque son las que hacen que la pantalla sirva con el
 * cliente real (unidades a decenas de consultas por mes, medido en producción):
 *
 * 1. **El eje se escala al máximo del período**, no a un máximo fijo. Un cliente con 3 consultas
 *    semanales tiene que mostrar su forma, no una línea aplastada contra el piso.
 * 2. Como esa escala es relativa, **cada punto lleva su número encima** (lo pinta el componente a
 *    partir de `points[].value`): sin eso, 3 consultas y 300 dibujan exactamente la misma curva.
 */
export function buildWeeklyChart(weeks, box = CHART_BOX) {
    const series = normalizeWeeks(weeks);
    const x0 = box.left;
    const x1 = box.width - box.right;
    const y0 = box.top;
    const y1 = box.height - box.bottom;
    const dx = (x1 - x0) / (series.length - 1);
    // El `1` evita dividir por cero con la serie vacía y deja el eje en 0..1 (todos los puntos en
    // el piso). Ese caso no se dibuja como línea: el componente muestra el vacío.
    const max = Math.max.apply(null, series.concat([1]));

    const points = series.map((value, index) => ({
        index,
        value,
        x: x0 + (index * dx),
        y: y1 - ((value / max) * (y1 - y0)),
    }));

    const line = points
        .map((point, index) => `${index ? 'L' : 'M'}${round1(point.x)} ${round1(point.y)}`)
        .join(' ');

    return {
        box,
        series,
        max,
        points,
        plot: {x0, x1, y0, y1, dx},
        linePath: line,
        areaPath: `${line} L${round1(x1)} ${round1(y1)} L${round1(x0)} ${round1(y1)} Z`,
        levels: chartGridLevels(max).map(value => ({value, y: y1 - ((value / max) * (y1 - y0))})),
        isEmpty: weeksAreEmpty(series),
    };
}

// --- Sparkline ----------------------------------------------------------------------------------

// La misma serie en chico, para la card del Panel y para cada fila del ranking. Sin librería: el
// repo ya tomó esa decisión para trazados de este tamaño (`agency/v2/Sparkline.js`).
export const SPARK_BOX = {width: 104, height: 26, pad: 2};

// La misma caja partida en dos, para apilar el chat y los ingresos a informes en una sola celda del
// ranking. Cada mitad se dibuja con SU PROPIO máximo, y eso es deliberado: un sparkline sin números
// comunica FORMA, y la magnitud ya vive en las dos columnas numéricas de al lado. Compartir escala
// aplastaría la serie chica contra el piso y diría "no entró a informes" de un cliente que sí entró.
export const SPARK_BOX_DUAL = {width: 104, height: 13, pad: 1.5};

/**
 * La geometría del sparkline, o `null` si la serie está entera en cero.
 *
 * El `null` es parte del diseño, no un borde: el llamador pinta un guion. Una línea plana pegada
 * al piso se lee como "usó poquito", cuando el dato real es "no usó nada".
 */
export function buildSparkline(weeks, box = SPARK_BOX) {
    const series = normalizeWeeks(weeks);
    if (weeksAreEmpty(series)) return null;

    const max = Math.max.apply(null, series.concat([1]));
    const dx = box.width / (series.length - 1);
    const floor = box.height - box.pad;
    const usableHeight = box.height - (box.pad * 3);

    const points = series.map((value, index) => ({
        x: index * dx,
        y: floor - ((value / max) * usableHeight),
    }));

    const line = points
        .map((point, index) => `${index ? 'L' : 'M'}${round1(point.x)} ${round1(point.y)}`)
        .join(' ');
    const last = points[points.length - 1];

    return {
        box,
        max,
        linePath: line,
        areaPath: `${line} L${round1(box.width)} ${box.height} L0 ${box.height} Z`,
        last: {x: round1(last.x), y: round1(last.y)},
    };
}

// --- Navegación dentro del portal ---------------------------------------------------------------

// Las superficies del portal que el backend marca como adopción (`PortalSurfaceActivity`). El
// listado de reportes no está: entrar a la lista es contexto, abrir un reporte es uso.
export const PORTAL_SURFACE = {
    REPORT_VIEW: 'REPORT_VIEW',
    DASHBOARD: 'DASHBOARD',
    MEMORY: 'MEMORY',
    CONNECTIONS: 'CONNECTIONS',
};

// Orden de la tabla: primero los reportes (que van itemizados, uno por fila), después el resto.
const SURFACE_ORDER = [PORTAL_SURFACE.DASHBOARD, PORTAL_SURFACE.MEMORY, PORTAL_SURFACE.CONNECTIONS];

/**
 * Las filas de "Qué abrió en el portal", ya compuestas.
 *
 * Los reportes viajan DOS veces en el payload: agregados en `by_surface` (`REPORT_VIEW`) y uno por
 * uno en `top_reports`. La tabla muestra los individuales —"abrió Rendimiento mensual 18 veces" es
 * accionable, "abrió reportes 28 veces" no—, así que la fila agregada de `REPORT_VIEW` se descarta:
 * dejarla sumaría los mismos ingresos dos veces en la misma tabla.
 */
export function buildPortalSurfaceRows(portalWeb) {
    const data = portalWeb || {};
    const bySurface = {};
    (data.by_surface || []).forEach((row) => {
        if (row && row.surface) bySurface[row.surface] = row;
    });

    const reports = (data.top_reports || []).map(report => ({
        key: `report-${report.report_id}`,
        surface: PORTAL_SURFACE.REPORT_VIEW,
        reportId: report.report_id,
        name: report.name || null,
        views: report.views || 0,
        activeDays: null, // el desglose por reporte no trae días; la columna muestra "—"
        lastAt: report.last_at || null,
    }));

    const others = SURFACE_ORDER
        .filter(surface => bySurface[surface])
        .map(surface => ({
            key: `surface-${surface}`,
            surface,
            reportId: null,
            name: null,
            views: bySurface[surface].views || 0,
            activeDays: bySurface[surface].active_days || 0,
            lastAt: bySurface[surface].last_at || null,
        }));

    return reports.concat(others);
}

// --- Ranking cross-cliente ----------------------------------------------------------------------

// Los filtros de la vista de todos los clientes. Son los mismos buckets que cuenta el funnel del
// encabezado, porque los cuatro números son CLICKEABLES: un número en un encabezado que no se
// puede seguir es una pregunta sin respuesta.
export const USAGE_FILTER = {
    ALL: 'ALL',
    ACTIVE: USAGE_STATE.ACTIVE,
    INVITED_UNUSED: USAGE_STATE.INVITED_UNUSED,
    DORMANT: USAGE_STATE.DORMANT,
    NO_USERS: USAGE_STATE.NO_USERS,
    MODULE_OFF: USAGE_STATE.MODULE_OFF,
    // Pseudo-estado: NO es un `state` del backend. Un cliente puede estar ACTIVE por el chat y
    // tener la puerta de los informes cerrada al mismo tiempo, así que esto es ortogonal al
    // estado y `filterClients` lo resuelve por su propia rama.
    REPORTS_BLOCKED: 'REPORTS_BLOCKED',
};

export const USAGE_SORT = {
    ATTENTION: 'ATTENTION',
    QUERIES: 'QUERIES',
    REPORT_VIEWS: 'REPORT_VIEWS',
    SHARED: 'SHARED',
    LAST: 'LAST',
    NAME: 'NAME',
};

// El orden por defecto NO es alfabético ni por volumen. Una lista ordenada por consultas pone
// arriba a los clientes que ya están bien; lo accionable está en el otro extremo. Primero el que
// compartió informes que nadie puede ver (se arregla con un click y el valor ya está creado),
// después el que usó y dejó de usar (una llamada), después el que nunca entró (un recordatorio),
// después los que funcionan, y al final los que ni siquiera tienen a nadie invitado o no tienen
// portal.
const REPORTS_BLOCKED_RANK = -1;
const ATTENTION_RANK = {
    [USAGE_STATE.DORMANT]: 0,
    [USAGE_STATE.INVITED_UNUSED]: 1,
    [USAGE_STATE.ACTIVE]: 2,
    [USAGE_STATE.NO_USERS]: 3,
    // Último: con el estado generalizado, `MODULE_OFF` dejó de ser un dato débil ("no tiene chat")
    // y pasó a ser uno fuerte ("no tiene portal, decisión deliberada de la agencia"). Es lo menos
    // accionable de la lista.
    [USAGE_STATE.MODULE_OFF]: 4,
};

// --- Informes compartidos ------------------------------------------------------------------------

/** Los ingresos a informes del período. `|| 0` porque el backend puede no mandarlo todavía. */
export function clientReportViewCount(client) {
    return (client && client.report_views) || 0;
}

/** Cuántos informes tiene compartidos. Es un STOCK, no depende del período elegido. */
export function clientSharedReportCount(client) {
    return (client && client.shared_reports) || 0;
}

// `=== false` y NO `!`: durante la ventana de skew el backend viejo no manda estos campos, y con
// `!undefined` la pantalla entera diría "chat apagado" para todos los clientes de la agencia.
export function chatIsOff(client) {
    return Boolean(client) && client.connect_enabled === false;
}

export function reportsAreOff(client) {
    return Boolean(client) && client.reports_enabled === false;
}

/**
 * La puerta de entrada cerrada: compartiste informes y el cliente no los ve.
 *
 * Es el patrón que se reportó tres veces (Brújula, Bakián, JT Ads): la pantalla del cliente
 * funciona pero no tiene ítem en el nav, así que nadie llega. Se mira desde las dos capas que lo
 * pueden cerrar — el toggle de ese cliente y el plan de la agencia — y devuelve cuál de las dos es,
 * porque se arreglan en lugares distintos.
 *
 * Devuelve un DESCRIPTOR, no un texto traducido: este módulo no tiene i18n adentro.
 */
export const REPORTS_GAP = {
    CLIENT_MODULE_OFF: 'CLIENT_MODULE_OFF',
    AGENCY_PLAN: 'AGENCY_PLAN',
};

export function clientReportsGap(client, {agencyReportsModule = true} = {}) {
    if (!clientSharedReportCount(client)) return null;
    if (reportsAreOff(client)) return REPORTS_GAP.CLIENT_MODULE_OFF;
    // `=== false` otra vez: sin el campo (backend viejo) no se puede afirmar que el plan bloquea.
    if (agencyReportsModule === false) return REPORTS_GAP.AGENCY_PLAN;
    return null;
}


/** Cuántos clientes hay en cada bucket, contados sobre las filas que efectivamente se listan. */
export function countByState(clients) {
    const counts = {};
    (clients || []).forEach((row) => {
        const state = row && row.state;
        if (state) counts[state] = (counts[state] || 0) + 1;
    });
    return counts;
}

/**
 * Filtro por estado y por texto, 100 % client-side sobre el payload.
 *
 * Los clientes sin uso viajan en la respuesta en cero y NO se omiten: la lista de los que *no*
 * adoptaron es la accionable. Filtrarlos del lado del servidor sería perder justo eso.
 */
export function filterClients(clients, {state, search, agencyReportsModule} = {}) {
    const needle = (search || '').trim().toLowerCase();
    return (clients || []).filter((row) => {
        if (state === USAGE_FILTER.REPORTS_BLOCKED) {
            // No es un `state` del backend: se calcula. Va antes de la comparación de abajo, que
            // nunca matchearía.
            if (!clientReportsGap(row, {agencyReportsModule})) return false;
        } else if (state && state !== USAGE_FILTER.ALL && row.state !== state) {
            return false;
        }
        if (!needle) return true;
        return String(row.name || '').toLowerCase().includes(needle);
    });
}

/** Ordena SIN mutar. El desempate es siempre el nombre, para que el orden sea estable. */
export function sortClients(clients, sort = USAGE_SORT.ATTENTION, {agencyReportsModule} = {}) {
    const rows = (clients || []).slice();
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
    const byDesc = read => (a, b) => (read(b) - read(a)) || byName(a, b);

    if (sort === USAGE_SORT.NAME) return rows.sort(byName);

    if (sort === USAGE_SORT.QUERIES) {
        // `clientQueryCount` cae al campo viejo si el backend todavía no manda el split. Sin ese
        // fallback, durante el skew la lista entera se ordenaría por 0 y el usuario vería un orden
        // alfabético disfrazado de ranking.
        return rows.sort(byDesc(clientQueryCount));
    }

    if (sort === USAGE_SORT.REPORT_VIEWS) return rows.sort(byDesc(clientReportViewCount));
    if (sort === USAGE_SORT.SHARED) return rows.sort(byDesc(clientSharedReportCount));

    if (sort === USAGE_SORT.LAST) {
        // Los que NUNCA tuvieron actividad van al final, no hundidos entre los más viejos: `|| 0`
        // los mandaría al fondo mezclados con los que sí usaron alguna vez, que son otra
        // conversación (a uno le recordás la invitación, al otro lo llamás).
        return rows.sort((a, b) => {
            const stampA = Date.parse(a.last_activity_at || '');
            const stampB = Date.parse(b.last_activity_at || '');
            const hasA = !Number.isNaN(stampA);
            const hasB = !Number.isNaN(stampB);
            if (hasA !== hasB) return hasA ? -1 : 1;
            if (hasA && stampA !== stampB) return stampB - stampA;
            return byName(a, b);
        });
    }

    // Sólo la capa 1 sube al tope. El bloqueo por PLAN afecta por igual a todos los que
    // compartieron algo, así que subirlos aplanaría el orden por atención justo cuando más se
    // necesita —y no hay nada que hacer fila por fila: se arregla una vez, en la suscripción.
    const rankOf = (row) => {
        if (clientReportsGap(row, {agencyReportsModule}) === REPORTS_GAP.CLIENT_MODULE_OFF) {
            return REPORTS_BLOCKED_RANK;
        }
        return ATTENTION_RANK[row.state] === undefined ? 9 : ATTENTION_RANK[row.state];
    };

    return rows.sort((a, b) => {
        const rankA = rankOf(a);
        const rankB = rankOf(b);
        if (rankA !== rankB) return rankA - rankB;
        // Dentro de los dormidos, primero el que hace MÁS tiempo que no vuelve: es el que está más
        // cerca de irse. Los que nunca tuvieron actividad van al final del grupo.
        if (a.state === USAGE_STATE.DORMANT) {
            const daysA = daysSince(a.last_activity_at);
            const daysB = daysSince(b.last_activity_at);
            if (daysA !== daysB) return (daysB === null ? -1 : daysB) - (daysA === null ? -1 : daysA);
        }
        return byName(a, b);
    });
}

/** Las iniciales del cliente para el avatar de la fila. */
export function clientInitials(name) {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '·';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

// --- Turnos: una consulta y los pasos que la rodean ----------------------------------------------

// La unidad del feed es el TURNO, no la sesión MCP. Una sesión es *por conexión*: una tarde de
// trabajo con 20 preguntas es un solo `session_id`, y mostrarla como una fila colapsada no dice
// nada. Un turno es lo que la agencia cree estar leyendo — una fila = una cosa que el cliente quiso
// saber, con los pasos del agente plegados detrás.
export const TURN_KIND = {
    QUESTION: 'QUESTION',   // hubo una pregunta en lenguaje natural
    DATA: 'DATA',           // consulta estructurada, sin pregunta escrita
    CONTEXT: 'CONTEXT',     // sólo pasos: el agente trabajó y no llegó a consultar
};

// Cuántos turnos se muestran de entrada y cuántos suma cada "Ver más". El backend manda todos los
// que viajan en UNA respuesta, así que "Ver más" revela lo que ya está en memoria: nunca duplica ni
// pierde filas.
export const TURNS_PAGE_SIZE = 8;

/**
 * Qué se muestra como encabezado de un turno. Devuelve un DESCRIPTOR, no un string: así este módulo
 * sigue sin i18n adentro y el componente decide el copy.
 *
 * ⚠️ Nunca mira `steps` para elegir la frase. Ese era exactamente el bug: el anticipo de una
 * conversación salía de su interacción más VIEJA, que casi siempre es un paso de contexto sin
 * texto, y toda la charla quedaba sin anticipo aunque adentro hubiera tres preguntas legibles.
 */
export function turnHeadline(turn, {textVisible} = {}) {
    if (!turn) return {kind: 'MISSING'};
    const text = typeof turn.query_text === 'string' ? turn.query_text.trim() : '';
    if (text) return {kind: 'TEXT', text};
    if (turn.kind === TURN_KIND.CONTEXT) return {kind: 'CONTEXT', steps: turn.steps_count};
    if (turn.shape) return {kind: 'SUMMARY', summary: turn.shape};
    // Sin texto y sin forma: hay que decir POR QUÉ. Con la policy apagada el backend nunca manda
    // el texto (aunque exista); con la policy encendida, esta consulta no dejó ninguno.
    return {kind: textVisible === false ? 'HIDDEN' : 'MISSING'};
}

/**
 * Los turnos del payload, tolerando el contrato viejo.
 *
 * El backend manda `turns` y —durante un release— también `sessions`. Si sólo llega `sessions`
 * (frontend nuevo contra backend viejo), se derivan turnos de lo que hay: cada interacción CON
 * texto es su propio turno y las que no tienen se pliegan en uno de contexto. No se inventa la
 * atribución «este paso pertenece a esa pregunta» — en el payload viejo esa relación no existe y
 * adivinarla contaría una historia falsa.
 */
export function normalizeTurns(usage) {
    if (!usage) return [];
    if (Array.isArray(usage.turns)) return usage.turns;
    if (!Array.isArray(usage.sessions)) return [];
    const turns = [];
    usage.sessions.forEach((session) => {
        // El backend viejo manda las preguntas de más nueva a más vieja.
        const questions = ((session && session.questions) || []).slice().reverse();
        const orphanSteps = [];
        questions.forEach((question) => {
            const text = typeof question.query_text === 'string' ? question.query_text.trim() : '';
            if (text) {
                turns.push(legacyTurn(session, question, TURN_KIND.QUESTION, text, []));
            } else {
                orphanSteps.push(legacyStep(question));
            }
        });
        if (orphanSteps.length) {
            // El id sale del último PASO, no de la última pregunta: si lo tomara de ahí, en una
            // sesión que termina con una pregunta con texto los dos turnos compartirían id y React
            // colapsaría uno de los dos (keys duplicadas).
            const last = orphanSteps[orphanSteps.length - 1];
            turns.push(legacyTurn(session, last, TURN_KIND.CONTEXT, null, orphanSteps));
        }
    });
    return turns.reverse();
}

function legacyTurn(session, question, kind, text, steps) {
    return {
        id: (question && question.id) || (session && session.session_id),
        session_id: session && session.session_id,
        source: session && session.session_id ? 'MCP' : 'REST_API',
        kind,
        at: question && question.at,
        started_at: (steps[0] && steps[0].at) || (question && question.at),
        ended_at: question && question.at,
        user_id: session && session.user_id,
        user_name: session && session.user_name,
        query_text: text,
        shape: null,
        tool_name: question && question.tool_name,
        rows: question && question.rows,
        duration_ms: question && question.duration_ms,
        failed: Boolean(question && question.failed),
        error_code: null,
        steps,
        steps_count: steps.length,
        truncated: false,
    };
}

function legacyStep(question) {
    return {
        id: question.id,
        at: question.at,
        action: question.action || null,
        tool_name: question.tool_name,
        role: 'CONTEXT',
        failed: Boolean(question.failed),
        error_code: null,
        duration_ms: question.duration_ms,
    };
}

/** La clave de React de un turno. El `id` es la fila que lo ancla, así que es estable entre fetches. */
export function turnKey(turn, index) {
    return (turn && turn.id) || `turn-${index}`;
}

// --- Contadores: consultas vs pasos --------------------------------------------------------------

/**
 * Las consultas del cliente. Con el backend viejo cae a `interactions`, que es el número inflado de
 * siempre — peor que el nuevo, pero nunca inventado.
 */
export function clientQueryCount(usage) {
    const counters = assistantClient(usage);
    if (!counters) return 0;
    return typeof counters.queries === 'number' ? counters.queries : (counters.interactions || 0);
}

/**
 * Los pasos que dio el asistente. Devuelve `null` —no cero— cuando el backend todavía no los manda:
 * es lo que permite que la pantalla no afirme un reparto que no tiene. Toda copy que hable de
 * «N consultas + M pasos» se renderiza sólo si esto es un número.
 */
export function clientStepCount(usage) {
    const counters = assistantClient(usage);
    if (!counters || typeof counters.steps !== 'number') return null;
    return counters.steps;
}

function assistantClient(usage) {
    if (!usage) return null;
    if (usage.assistant && usage.assistant.client) return usage.assistant.client;
    // La card del Panel y el ranking aplanan los contadores en la raíz.
    if (typeof usage.client_queries === 'number') {
        return {queries: usage.client_queries, steps: usage.client_steps, interactions: undefined};
    }
    return null;
}

/** Cada cuántos días de la semana, en promedio, el cliente consulta. Para el subtítulo de temas. */
export function activeDaysPerWeek(activeDays, windowDays) {
    if (!activeDays || !windowDays) return 0;
    return round1((activeDays / windowDays) * 7);
}

// --- Qué hizo el asistente: acciones y temas -----------------------------------------------------

// El backend manda enums (`action`, `topic`) y acá se traducen. Es el mismo criterio que `state` y
// `PORTAL_SURFACE`: el `tool_name` es un identificador interno (`dashbo_list_client_available_fields`)
// y no superficie de producto.
//
// Los verbos están en PASADO y el sujeto es el ASISTENTE, no el cliente: el panel que los muestra se
// titula «Lo que hizo el asistente para responder».
const STEP_ACTION_LABELS = {
    QUERY_DATA: {key: 'portal_usage.step_action_query_data', fallback: 'Consultó los datos'},
    PARSE_QUERY: {key: 'portal_usage.step_action_parse_query', fallback: 'Tradujo la pregunta a una consulta'},
    LIST_CLIENTS: {key: 'portal_usage.step_action_list_clients', fallback: 'Buscó de qué cliente se trataba'},
    LIST_FIELDS: {key: 'portal_usage.step_action_list_fields', fallback: 'Miró qué métricas hay disponibles'},
    LIST_LINES: {key: 'portal_usage.step_action_list_lines', fallback: 'Buscó las líneas de seguimiento'},
    READ_LINE: {key: 'portal_usage.step_action_read_line', fallback: 'Revisó una línea de seguimiento'},
    READ_GOALS: {key: 'portal_usage.step_action_read_goals', fallback: 'Revisó objetivos y presupuesto'},
    READ_CONTEXT: {key: 'portal_usage.step_action_read_context', fallback: 'Leyó el contexto del cliente'},
    READ_ACTIVITY: {key: 'portal_usage.step_action_read_activity', fallback: 'Revisó la bitácora del cliente'},
    SEARCH_MEMORY: {key: 'portal_usage.step_action_search_memory', fallback: 'Buscó en la memoria del cliente'},
    READ_DOCUMENT: {key: 'portal_usage.step_action_read_document', fallback: 'Abrió un documento cargado'},
    SAVE_MEMORY: {key: 'portal_usage.step_action_save_memory', fallback: 'Guardó un dato en la memoria'},
    SAVE_MEETING: {key: 'portal_usage.step_action_save_meeting', fallback: 'Guardó una reunión'},
    SAVE_SUMMARY: {key: 'portal_usage.step_action_save_summary', fallback: 'Guardó el resumen de la sesión'},
    OTHER: {key: 'portal_usage.step_action_other', fallback: 'Otra acción del asistente'},
};

/**
 * La etiqueta de un paso, más si la conocemos.
 *
 * Con `known: false` el componente pinta el copy genérico Y el `tool_name` crudo: una fila que
 * desaparece se lee como «no lo hizo», que es mentira; una con el nombre técnico se lee como «esto
 * es nuevo», que es la verdad. Mismo criterio que `resolveUsageState`, que ante un valor
 * desconocido devuelve `null` en vez de inventar.
 */
export function resolveStepAction(step) {
    const action = step && step.action;
    const known = Boolean(action && STEP_ACTION_LABELS[action]);
    return {...(STEP_ACTION_LABELS[action] || STEP_ACTION_LABELS.OTHER), known};
}

// Los temas del bloque «Para qué lo usa». Un tema que este frontend no conozca se pliega dentro de
// «Otros temas» sumando su conteo: un enum crudo en una lista rankeada sería basura visible, a
// diferencia de un paso suelto donde el nombre técnico sí informa.
const TOPIC_LABELS = {
    DATA_QUERY: {key: 'portal_usage.topic_data_query', fallback: 'Consultar sus números'},
    CATALOG: {key: 'portal_usage.topic_catalog', fallback: 'Ver qué datos hay disponibles'},
    BUDGET_GOALS: {key: 'portal_usage.topic_budget_goals', fallback: 'Presupuesto y objetivos'},
    MEMORY_READ: {key: 'portal_usage.topic_memory_read', fallback: 'Contexto y memoria del cliente'},
    MEMORY_WRITE: {key: 'portal_usage.topic_memory_write', fallback: 'Cargar información'},
    // `dashbo_list_prompts` / `dashbo_get_prompt` (y sus gemelas REST). Es la señal de que el
    // cliente está usando los prompts que la agencia publicó en su portal: plegado dentro de
    // «Otros temas» esa lectura se pierde.
    PROMPTS: {key: 'portal_usage.topic_prompts', fallback: 'Pedir los prompts de tu agencia'},
    OTHER: {key: 'portal_usage.topics_more', fallback: 'Otros temas'},
};

export function resolveTopic(topic) {
    return TOPIC_LABELS[topic] || null;
}

/**
 * Las filas del bloque de temas, con el ancho de barra ya calculado.
 *
 * Los temas desconocidos se acumulan en «Otros temas» en vez de descartarse: el total tiene que
 * seguir cerrando contra lo que el cliente hizo.
 */
export function buildTopicRows(assistant) {
    const rows = (assistant && assistant.topics) || [];
    const known = [];
    let other = 0;
    rows.forEach((row) => {
        const count = (row && row.interactions) || 0;
        if (!count) return;
        if (resolveTopic(row.topic) && row.topic !== 'OTHER') known.push({topic: row.topic, count});
        else other += count;
    });
    if (other) known.push({topic: 'OTHER', count: other});
    const max = known.reduce((top, row) => Math.max(top, row.count), 0);
    return known
        .sort((a, b) => b.count - a.count)
        .map(row => ({...row, ratio: max ? row.count / max : 0}));
}

// --- Formato de tiempo (compartido por el feed) --------------------------------------------------

/** "hoy 09:14" / "8 de agosto 11:27" — relativo mientras sea útil, fecha cuando ya no lo es. */
export function whenLabel(iso, t) {
    const date = parsePanelDate(iso);
    if (!date) return '';
    const relative = relativeTimeLabel(iso, t);
    const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    return relative ? `${relative} ${clock}` : clock;
}

/**
 * "1,8 s" / "3 min".
 *
 * El separador decimal sale del idioma de la UI: un `.replace('.', ',')` deja "1,8 s" también en
 * inglés, donde se lee como mil ochocientos.
 */
export function durationLabel(ms, t, language) {
    const value = Number(ms);
    if (!Number.isFinite(value) || value <= 0) return '';
    if (value < 60000) {
        const seconds = value / 1000;
        const shown = seconds < 10
            ? formatDecimal(seconds, language)
            : String(Math.round(seconds));
        return t('portal_usage.duration_seconds', '{{n}} s', {n: shown});
    }
    return t('portal_usage.duration_minutes', '{{n}} min', {n: Math.round(value / 60000)});
}

// --- La frase de una consulta estructurada -------------------------------------------------------

// Cuántos campos se nombran antes de resumir. Una consulta con 11 campos no puede ocupar tres
// líneas de un feed.
const SUMMARY_MAX_FIELDS = 3;

/**
 * «Costo y Clics de Meta · últimos 30 días» a partir de las partes que manda el backend.
 *
 * El backend manda las PARTES (nombres del catálogo, canal, rango) y acá se compone: mandar la
 * frase armada la dejaría en español en la UI inglesa.
 *
 * Cuatro formas y no una key con variables opcionales: un `de {{scope}}` vacío produce
 * «Costo y Clics de  · últimos 30 días».
 *
 * ⚠️ `escapeValue: false` porque un nombre de campo puede traer `/` o `&` y saldría como entidad
 * HTML. Es seguro SÓLO porque el resultado se renderiza como children de React, nunca por
 * `dangerouslySetInnerHTML`.
 */
export function buildDataSummary(summary, t, language) {
    if (!summary) return '';
    const fields = fieldNames(summary, t);
    const scope = scopeLabel(summary);
    const range = rangeLabel(summary, t, language);
    const options = {interpolation: {escapeValue: false}};

    if (fields && scope && range) {
        return t('portal_usage.turn_data_full', '{{fields}} de {{scope}} · {{range}}',
            {fields, scope, range, ...options});
    }
    if (fields && range) {
        return t('portal_usage.turn_data_no_scope', '{{fields}} · {{range}}', {fields, range, ...options});
    }
    if (fields && scope) {
        return t('portal_usage.turn_data_no_range', '{{fields}} de {{scope}}', {fields, scope, ...options});
    }
    if (fields) return fields;
    if (scope && range) {
        return t('portal_usage.turn_data_no_scope', '{{fields}} · {{range}}',
            {fields: scope, range, ...options});
    }
    return scope || range || '';
}

function fieldNames(summary, t) {
    const fields = (summary.fields || []).map(field => (
        field.name || (field.kind === 'CUSTOM_METRIC'
            ? t('portal_usage.field_custom_metric', 'Métrica personalizada')
            : field.id)
    )).filter(Boolean);
    if (!fields.length) return '';
    const total = summary.fields_total || fields.length;
    const options = {interpolation: {escapeValue: false}};
    if (total > SUMMARY_MAX_FIELDS) {
        return t('portal_usage.turn_data_more_fields', '{{a}} y {{n}} campos más',
            {a: fields.slice(0, SUMMARY_MAX_FIELDS).join(', '), n: total - SUMMARY_MAX_FIELDS, ...options});
    }
    if (fields.length === 1) return fields[0];
    return t('portal_usage.list_and', '{{a}} y {{b}}',
        {a: fields.slice(0, -1).join(', '), b: fields[fields.length - 1], ...options});
}

// Los canales llegan como el MCP los loguea (`FACEBOOK`, no `META`), que es lo que espera
// `ChannelLogo`. Acá sólo se nombran; el logo lo pone quien lo renderiza.
const CHANNEL_NAMES = {
    GOOGLE: 'Google Ads',
    FACEBOOK: 'Meta',
    TIKTOK: 'TikTok',
    LINKEDIN: 'LinkedIn',
    MERCADO_ADS: 'Mercado Ads',
    ANALYTICS: 'GA4',
};

function scopeLabel(summary) {
    const channels = (summary.scope && summary.scope.channels) || [];
    if (!channels.length) return '';
    return channels.map(channel => CHANNEL_NAMES[channel] || channel).join(' + ');
}

/**
 * El período que pidió la consulta.
 *
 * ⚠️ `days` es la LONGITUD del rango, no «los últimos N días»: el backend manda
 * `{start, end, days}` con las fechas que el agente consultó. Rotular «últimos 31 días» a alguien
 * que el 15 de agosto preguntó por julio es mentirle sobre el período — y es la frase que la
 * agencia lee encima del ~71% del feed, porque las consultas estructuradas no traen pregunta.
 *
 * «Últimos N días» sólo se usa cuando el rango efectivamente termina hoy (o cuando no hay fechas
 * y `days` es todo lo que tenemos).
 */
function rangeLabel(summary, t, language) {
    const range = summary.date_range;
    if (!range) return '';
    const start = parsePanelDate(range.start);
    const end = parsePanelDate(range.end);

    if (start && end) {
        if (isWholeCalendarMonth(start, end)) {
            return start.toLocaleDateString(language || undefined, {month: 'long', year: 'numeric'});
        }
        if (!endsToday(end)) {
            return t('portal_usage.range_between', 'del {{from}} al {{to}}', {
                from: formatShortDate(range.start, language),
                to: formatShortDate(range.end, language),
                interpolation: {escapeValue: false},
            });
        }
    }

    if (!range.days) return '';
    if (range.days === 1) return t('portal_usage.range_one_day', 'un día');
    return t('portal_usage.range_last_days', 'últimos {{n}} días', {n: range.days});
}

function isWholeCalendarMonth(start, end) {
    const lastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    return start.getDate() === 1
        && end.getDate() === lastDay
        && start.getMonth() === end.getMonth()
        && start.getFullYear() === end.getFullYear();
}

function endsToday(end) {
    const today = new Date();
    return end.getDate() === today.getDate()
        && end.getMonth() === today.getMonth()
        && end.getFullYear() === today.getFullYear();
}

// --- Auxiliares ---------------------------------------------------------------------------------

function pad(value) {
    return String(value).padStart(2, '0');
}

function round1(value) {
    return Math.round(value * 10) / 10;
}
